import React, { useState, useEffect } from "react";
import { sitePath } from "../../lib/siteRouting";
import { Link, useLocation } from "react-router-dom";
import {
  Clock, Mail, MapPin, Facebook, Instagram,
  Calendar, User, ShoppingBag, Phone, MessageCircle,
  Menu, X, Search, ChevronDown
} from "lucide-react";
import { ORG_JSONLD } from "./SEOHead";
import { TpcoLogo } from "./TpcoLogo";
import { PageTransition } from "./PageTransition";
import CookieConsent from "./CookieConsent";
import AnalyticsLoader from "./AnalyticsLoader";
import ExitIntentPopup from "./ExitIntentPopup";
import ProjectsMegaMenu from "./ProjectsMegaMenu";
import { CONTACT_PHONE as PHONE, waLink } from "../../config/contact";

const WA_LINK = waLink("Bonjour TPCO Senegal");

const NAV = [
  { to: sitePath(""), label: "Accueil", testid: "site-nav-accueil", end: true },
  { to: sitePath("/qui-sommes-nous"), label: "Qui sommes-nous", testid: "site-nav-qui-sommes-nous" },
  { to: sitePath("/projets"), label: "Nos projets", testid: "site-nav-nos-projets", hasMenu: true },
  { to: sitePath("/equipements"), label: "Nos équipements", testid: "site-nav-nos-equipements" },
  { to: sitePath("/contact"), label: "Contact", testid: "site-nav-contact" },
];

export default function SiteLayout({ children }) {
  const loc = useLocation();
  const isActive = (p, end = false) => end ? loc.pathname === p : loc.pathname === p || loc.pathname.startsWith(p + "/");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);

  useEffect(() => {
    let el = document.head.querySelector('script[id="org-jsonld"]');
    if (!el) {
      el = document.createElement("script");
      el.setAttribute("type", "application/ld+json");
      el.setAttribute("id", "org-jsonld");
      el.textContent = JSON.stringify(ORG_JSONLD);
      document.head.appendChild(el);
    }
  }, []);

  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  return (
    <div className="min-h-screen bg-white text-[#0F1A3D]" style={{ fontFamily: "'Manrope', 'Inter', system-ui, sans-serif" }}>
      {/* ===== Top utility bar — style Maisons-Pierre ===== */}
      <div className="bg-white border-b border-[#E2E5F1]">
        <div className="max-w-[1400px] mx-auto px-4 lg:px-10 h-12 flex items-center justify-between gap-2">
          {/* Left : utility links */}
          <div className="hidden md:flex items-center gap-1 lg:gap-2 text-[12px] text-[#0F1A3D]">
            <a href={sitePath("/contact")} className="inline-flex items-center gap-1.5 px-2 lg:px-3 h-10 rounded hover:bg-[#FCE4EC] transition group" data-testid="util-rdv">
              <Calendar className="w-4 h-4 text-[#E91E63]" />
              <span className="font-semibold">Prendre rendez-vous</span>
            </a>
            <Link to={sitePath("/projets")} className="inline-flex items-center gap-1.5 px-2 lg:px-3 h-10 rounded hover:bg-[#FCE4EC] transition" data-testid="util-projets">
              <MapPin className="w-4 h-4 text-[#E91E63]" />
              <span className="font-semibold">Nos projets</span>
            </Link>
            <a href="https://monespace.tpco.sn" className="inline-flex items-center gap-1.5 px-2 lg:px-3 h-10 rounded hover:bg-[#FCE4EC] transition" data-testid="util-espace-client">
              <User className="w-4 h-4 text-[#E91E63]" />
              <span className="font-semibold">Espace client</span>
            </a>
            <a href="https://vente.tpco.sn" className="inline-flex items-center gap-1.5 px-2 lg:px-3 h-10 rounded hover:bg-[#FCE4EC] transition" data-testid="util-catalogue">
              <ShoppingBag className="w-4 h-4 text-[#E91E63]" />
              <span className="font-semibold">Catalogue</span>
            </a>
          </div>
          {/* Mobile : email/clock condensed */}
          <div className="md:hidden flex items-center gap-2 text-[11px] text-[#4B5775]">
            <Clock className="w-3 h-3 text-[#E91E63]" />
            <span>Lun–Sam 09h–17h</span>
          </div>
          {/* Right : phone + WA + socials */}
          <div className="flex items-center gap-2">
            <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="hidden sm:inline-flex items-center gap-2 h-10 px-3 lg:px-4 bg-[#E5F8E9] hover:bg-[#D4F0DC] border border-[#A8DCB4] rounded transition group" data-testid="util-phone">
              <Phone className="w-3.5 h-3.5 text-[#2D7A3F]" />
              <div className="flex flex-col leading-tight">
                <span className="font-extrabold text-[13px] text-[#0F1A3D]">{PHONE}</span>
                <span className="text-[8px] uppercase tracking-wider text-[#2D7A3F] font-bold">Service & appel gratuits</span>
              </div>
            </a>
            <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="sm:hidden h-9 w-9 rounded bg-[#25D366] text-white flex items-center justify-center" aria-label="WhatsApp">
              <MessageCircle className="w-4 h-4" />
            </a>
            <div className="hidden lg:flex items-center gap-1.5 ml-1">
              <a href="#" aria-label="Facebook" className="w-7 h-7 rounded-full bg-[#F1F3FB] hover:bg-[#1E3A8A] hover:text-white flex items-center justify-center transition"><Facebook className="w-3 h-3" /></a>
              <a href="#" aria-label="Instagram" className="w-7 h-7 rounded-full bg-[#F1F3FB] hover:bg-[#E91E63] hover:text-white flex items-center justify-center transition"><Instagram className="w-3 h-3" /></a>
            </div>
          </div>
        </div>
      </div>

      {/* ===== Main header (logo + nav + CTA) ===== */}
      <header className="sticky top-0 z-30 bg-white shadow-sm" onMouseLeave={() => setMegaOpen(false)}>
        <div className="max-w-[1400px] mx-auto pl-4 lg:pl-10 flex items-center justify-between">
          <Link to={sitePath("")} className="flex items-center gap-3 py-3" data-testid="site-logo">
            <TpcoLogo variant="light" size="lg" />
          </Link>

          <nav className="hidden lg:flex items-center" data-testid="site-main-nav">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                onMouseEnter={() => setMegaOpen(!!n.hasMenu)}
                onClick={() => setMegaOpen(false)}
                className={`px-4 py-7 text-[13px] font-bold uppercase tracking-wider transition ${isActive(n.to, n.end) ? "text-[#1E3A8A] border-b-2 border-[#E91E63]" : "text-[#0F1A3D] hover:text-[#1E3A8A] border-b-2 border-transparent"}`}
              >
                {n.label}{n.hasMenu && <ChevronDown className="w-3 h-3 inline ml-1" />}
              </Link>
            ))}
            <button className="ml-2 w-10 h-10 rounded-full hover:bg-[#F1F3FB] flex items-center justify-center text-[#0F1A3D]" aria-label="Rechercher" data-testid="site-search-btn">
              <Search className="w-4 h-4" />
            </button>
          </nav>

          {/* Mobile burger */}
          <button onClick={() => setMobileOpen(!mobileOpen)} className="lg:hidden p-4" aria-label="Menu" data-testid="site-mobile-menu-btn">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {mobileOpen && (
          <nav className="lg:hidden border-t border-[#E2E5F1] bg-white">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} className={`block px-6 py-3 text-sm font-bold uppercase tracking-wider border-b border-[#E2E5F1] ${isActive(n.to, n.end) ? "text-[#1E3A8A] bg-[#E8EBFA]" : "text-[#0F1A3D] hover:bg-[#F8FAFC]"}`}>
                {n.label}
              </Link>
            ))}
            <a href="https://monespace.tpco.sn" className="block px-6 py-3 text-sm font-bold uppercase tracking-wider border-b border-[#E2E5F1] text-[#E91E63]">Espace client →</a>
            <a href="https://vente.tpco.sn" className="block px-6 py-3 text-sm font-bold uppercase tracking-wider border-b border-[#E2E5F1] text-[#E91E63]">Catalogue de biens →</a>
            <Link to={sitePath("/contact")} className="block px-6 py-4 text-sm font-bold uppercase tracking-wider bg-[#1E3A8A] text-white text-center">Demander un devis</Link>
          </nav>
        )}

        {/* Mega-menu projets */}
        <ProjectsMegaMenu open={megaOpen} onClose={() => setMegaOpen(false)} />
      </header>

      <main><PageTransition>{children}</PageTransition></main>

      {/* ===== Mega footer ===== */}
      <footer className="bg-[#0F1A3D] text-white mt-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div>
            <TpcoLogo variant="dark" size="lg" className="mb-4" />
            <p className="text-xs text-white/70 leading-relaxed mt-2">Construire autrement et mieux. Courage en béton et mental d'acier.</p>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E91E63] font-bold mb-3">Navigation</div>
            <ul className="space-y-2 text-sm text-white/80">
              <li><Link to={sitePath("/qui-sommes-nous")} className="hover:text-white">Qui sommes-nous</Link></li>
              <li><Link to={sitePath("/projets")} className="hover:text-white">Nos projets</Link></li>
              <li><Link to={sitePath("/equipements")} className="hover:text-white">Nos équipements</Link></li>
              <li><Link to={sitePath("/contact")} className="hover:text-white">Contact</Link></li>
              <li><a href="https://vente.tpco.sn" className="hover:text-white">Catalogue de biens</a></li>
              <li><a href="https://monespace.tpco.sn" className="hover:text-white">Espace client</a></li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E91E63] font-bold mb-3">Contact</div>
            <ul className="space-y-2 text-sm text-white/80">
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 text-[#E91E63] mt-0.5 shrink-0" /> Hann Maristes 1, lot F/06/A, Dakar, Sénégal</li>
              <li className="flex items-center gap-2"><Phone className="w-4 h-4 text-[#E91E63]" /> <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="hover:text-white">{PHONE}</a></li>
              <li className="flex items-center gap-2"><Mail className="w-4 h-4 text-[#E91E63]" /> <a href="mailto:contact@tpco.sn" className="hover:text-white">contact@tpco.sn</a></li>
              <li className="flex items-center gap-2"><Clock className="w-4 h-4 text-[#E91E63]" /> Lun–Sam · 09h00 → 17h00</li>
            </ul>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#E91E63] font-bold mb-3">Espace client</div>
            <p className="text-xs text-white/70 mb-3">Suivez vos contrats, paiements et messages.</p>
            <a href="https://monespace.tpco.sn" className="inline-flex items-center gap-2 h-9 px-4 rounded-md bg-[#E91E63] hover:bg-[#D81B60] text-white text-xs font-semibold">Se connecter</a>
          </div>
        </div>
        <div className="border-t border-white/10">
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-5 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-white/50">
            <div>© {new Date().getFullYear()} TPCO Sénégal · Touba Pro Construction</div>
            <Link to="/login" className="hover:text-white">Espace équipe</Link>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp + Call buttons */}
      <div className="fixed bottom-5 right-5 z-40 flex flex-col gap-2.5">
        <a href={WA_LINK} target="_blank" rel="noopener noreferrer" className="w-14 h-14 bg-[#25D366] hover:bg-[#1FB855] text-white rounded-full shadow-lg flex items-center justify-center transition" aria-label="WhatsApp" data-testid="float-wa">
          <MessageCircle className="w-6 h-6" />
        </a>
        <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="lg:hidden w-14 h-14 bg-[#1E3A8A] hover:bg-[#0F1A3D] text-white rounded-full shadow-lg flex items-center justify-center transition" aria-label="Appeler" data-testid="float-call">
          <Phone className="w-5 h-5" />
        </a>
      </div>

      {/* Analytics (chargés uniquement si consentement) */}
      <AnalyticsLoader />
      {/* Pop-up d'intention de sortie */}
      <ExitIntentPopup />
      {/* Bandeau cookies RGPD */}
      <CookieConsent />
    </div>
  );
}
